import { MutableRefObject, useCallback, useEffect, useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'

import { useUpdateUnreadCountQueries } from './useUpdateUnreadCountQueries'
import { getFeedUnreadCountQueryOptions, useSaveLastReadActivity } from '../api/feed.service'
import { entityFeedQueryOptions } from '../api/useEntityFeed'
import { UnreadActivitiesCountList } from '../domain/userInteraction'

export const useMarkFeedAsRead = (feedRef: MutableRefObject<HTMLElement | null>, entityIDs: string[]) => {
  const queries = useQueries({
    /* Added "orderBy: _id" to ensure we fetch the most recently created feed entry. By default, feeds sort by activity.createdAt, 
   (when the activity happened), which can differ from the feed entry’s createdAt. Since count logic depends on feed _id, 
   we align ordering with _id to keep counts accurate. */
    queries: [
      getFeedUnreadCountQueryOptions(entityIDs),
      ...entityIDs.map((entityID) => entityFeedQueryOptions({ entityID: [entityID], pageSize: 1, orderBy: '_id' })),
    ],
  })

  const [unreadQueryData, mainEntityQueryData, additionalEntityQueryData] = queries

  const isQueriesFetching = queries.some((query) => query?.isFetching)
  const { mutateAsync: saveLastReadActivityAPI, isPending: isSaveLastReadActivityPending } = useSaveLastReadActivity()
  const updateUnreadCountQueries = useUpdateUnreadCountQueries()

  const saveLastReadActivity = useCallback(
    async ({ entityID, activityID }: { entityID: string; activityID: string }) => {
      await saveLastReadActivityAPI({ entityID, activityID })
      await updateUnreadCountQueries(entityID, activityID)
    },
    [saveLastReadActivityAPI, updateUnreadCountQueries]
  )

  const unreadCounts = unreadQueryData?.data as UnreadActivitiesCountList | undefined
  const mainEntity = mainEntityQueryData?.data
  const additionalEntity = additionalEntityQueryData?.data

  const queryData = useMemo(() => {
    return !isQueriesFetching ? (additionalEntity ? [mainEntity, additionalEntity] : [mainEntity]) : []
  }, [isQueriesFetching, mainEntity, additionalEntity])

  useEffect(() => {
    if (!isSaveLastReadActivityPending && queryData.length) {
      queryData.forEach(async (entityQuery) => {
        const latestActivityID = entityQuery?.results[0]?.id
        const entityID = entityQuery?.results[0]?.feedInfo.entityID
        if (entityID && latestActivityID && unreadCounts?.[entityID]?.activityID !== latestActivityID) {
          await saveLastReadActivity({ entityID, activityID: latestActivityID })
        }
      })
    }
  }, [unreadCounts, saveLastReadActivity, isSaveLastReadActivityPending, queryData, feedRef])

  return null
}
